<script setup>
import { ref, onMounted, computed } from 'vue';
import { useApi } from '@/composables/useApi.js';

const { get } = useApi();

const props = defineProps({
  technicianId: { type: String, default: null },
  ticketId: { type: Number, default: null }
});

const emit = defineEmits(['task-selected']);

// State
const tasks = ref([]);
const technicians = ref([]);
const isLoading = ref(false);
const error = ref(null);

// Filters
const statusFilter = ref('all');
const technicianFilter = ref('all');

// Computed
const filteredTasks = computed(() => {
  let result = [...tasks.value];

  if (statusFilter.value !== 'all') {
    result = result.filter(t => t.status === statusFilter.value);
  }

  if (technicianFilter.value !== 'all') {
    result = result.filter(t => t.technician_id === technicianFilter.value);
  }

  return result;
});

const statusCounts = computed(() => {
  const counts = { pending: 0, sent: 0, in_progress: 0, completed: 0 };
  tasks.value.forEach(t => {
    if (counts[t.status] !== undefined) counts[t.status]++;
  });
  return counts;
});

// Methods
async function loadTasks() {
  isLoading.value = true;
  error.value = null;

  try {
    let url = '/api/technician-tasks/list';
    const params = new URLSearchParams();

    if (props.technicianId) params.append('technicianId', props.technicianId);
    if (props.ticketId) params.append('ticketId', props.ticketId);

    if (params.toString()) url += `?${params.toString()}`;

    const data = await get(url);
    tasks.value = data.tasks || [];
  } catch (err) {
    error.value = err.message;
    console.error('Error loading tasks:', err);
  } finally {
    isLoading.value = false;
  }
}

async function loadTechnicians() {
  try {
    const data = await get('/api/technicians/list');
    technicians.value = data.technicians || [];
  } catch (err) {
    console.error('Error loading technicians:', err);
  }
}

function getStatusLabel(status) {
  const labels = {
    pending: 'Ceka',
    sent: 'Odeslano',
    in_progress: 'Probiha',
    completed: 'Dokonceno',
    cancelled: 'Zruseno'
  };
  return labels[status] || status;
}

function getStatusClass(status) {
  const classes = {
    pending: 'bg-yellow-100 text-yellow-800',
    sent: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-orange-100 text-orange-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-100 text-gray-800'
  };
  return classes[status] || 'bg-gray-100 text-gray-800';
}

function getPriorityClass(priority) {
  const classes = {
    low: 'text-gray-500',
    normal: 'text-blue-600',
    high: 'text-orange-600',
    urgent: 'text-red-600'
  };
  return classes[priority] || 'text-gray-500';
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function selectTask(task) {
  emit('task-selected', task);
}

// Init
onMounted(() => {
  loadTasks();
  loadTechnicians();
});

// Expose refresh method
defineExpose({ refresh: loadTasks });
</script>

<template>
  <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <!-- Header -->
    <div class="px-6 py-4 border-b border-gray-200">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-lg font-semibold text-gray-900">Ukoly pro techniky</h2>
          <p class="text-sm text-gray-500">{{ filteredTasks.length }} ukolu</p>
        </div>
        <button
          @click="loadTasks"
          class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
          title="Obnovit"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      <!-- Status badges -->
      <div class="flex flex-wrap gap-2 mb-4">
        <button
          @click="statusFilter = 'all'"
          :class="statusFilter === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
          class="px-3 py-1 rounded-full text-sm transition"
        >
          Vse ({{ tasks.length }})
        </button>
        <button
          @click="statusFilter = 'pending'"
          :class="statusFilter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'"
          class="px-3 py-1 rounded-full text-sm transition"
        >
          Ceka ({{ statusCounts.pending }})
        </button>
        <button
          @click="statusFilter = 'sent'"
          :class="statusFilter === 'sent' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'"
          class="px-3 py-1 rounded-full text-sm transition"
        >
          Odeslano ({{ statusCounts.sent }})
        </button>
        <button
          @click="statusFilter = 'in_progress'"
          :class="statusFilter === 'in_progress' ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-800 hover:bg-orange-200'"
          class="px-3 py-1 rounded-full text-sm transition"
        >
          Probiha ({{ statusCounts.in_progress }})
        </button>
        <button
          @click="statusFilter = 'completed'"
          :class="statusFilter === 'completed' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800 hover:bg-green-200'"
          class="px-3 py-1 rounded-full text-sm transition"
        >
          Hotovo ({{ statusCounts.completed }})
        </button>
      </div>

      <!-- Technician filter -->
      <div v-if="technicians.length > 0 && !props.technicianId" class="flex items-center gap-2">
        <label class="text-sm text-gray-600">Technik:</label>
        <select
          v-model="technicianFilter"
          class="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
        >
          <option value="all">Vsichni</option>
          <option v-for="tech in technicians" :key="tech.id" :value="tech.id">
            {{ tech.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="px-6 py-3 bg-red-50 text-red-700 text-sm">
      {{ error }}
    </div>

    <!-- Loading -->
    <div v-if="isLoading && tasks.length === 0" class="px-6 py-12 text-center text-gray-500">
      Nacitam...
    </div>

    <!-- Tasks list -->
    <div v-else class="divide-y divide-gray-100">
      <div
        v-for="task in filteredTasks"
        :key="task.id"
        @click="selectTask(task)"
        class="px-6 py-4 hover:bg-gray-50 cursor-pointer transition"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <span class="text-sm font-medium text-blue-600">#{{ task.ticket_id }}</span>
              <span :class="getStatusClass(task.status)" class="px-2 py-0.5 rounded text-xs">
                {{ getStatusLabel(task.status) }}
              </span>
              <span :class="getPriorityClass(task.priority)" class="text-xs">
                {{ task.priority === 'urgent' ? '!!!' : task.priority === 'high' ? '!!' : '' }}
              </span>
            </div>
            <h3 class="font-medium text-gray-900 truncate">{{ task.ticket_subject || 'Bez predmetu' }}</h3>
            <div class="flex items-center gap-4 mt-1 text-sm text-gray-500">
              <span class="flex items-center gap-1">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {{ task.technician_name }}
              </span>
              <span>{{ formatDate(task.assigned_at) }}</span>
            </div>
            <div v-if="task.customer_name" class="mt-1 text-sm text-gray-500">
              Zakaznik: {{ task.customer_name }}
            </div>
          </div>
          <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>

      <div v-if="filteredTasks.length === 0" class="px-6 py-12 text-center text-gray-500">
        Zadne ukoly k zobrazeni
      </div>
    </div>
  </div>
</template>
